import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, NonNullableFormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { UiService } from '../../core/services/ui.service';
import { EMAIL_REGEX } from '../../core/constants/validation.constants';
import { UserService } from '../../core/services/user.service';
import { AppButtonComponent } from '../../shared/components/button/app-button.component';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { AuthModalShellComponent } from '../../shared/components/auth-modal-shell/auth-modal-shell.component';
import { AuthEmailFieldComponent } from '../../shared/components/auth-email-field/auth-email-field.component';
import { SnackbarService } from '../../core/services/snackbar.service';

type ResetPasswordForm = {
  email: FormControl<string>;
};

@Component({
  selector: 'app-reset-password-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AppButtonComponent,
    ConfirmDialogComponent,
    AuthModalShellComponent,
    AuthEmailFieldComponent,
  ],
  templateUrl: './reset-password-modal.component.html',
  styleUrls: ['./reset-password-modal.component.scss'],
})
export class ResetPasswordModalComponent implements OnDestroy {
  isVisible = false;

  isSuccessModalVisible = false;

  isLoading = false;
  errorMessage = '';
  isConfirmLoading = false;

  resetPasswordForm: FormGroup<ResetPasswordForm>;
  private subscription = new Subscription();

  constructor(
    private uiService: UiService,
    private fb: NonNullableFormBuilder,
    private userManagementService: UserService,
    private snackbarService: SnackbarService,
  ) {
    const modalSub = this.uiService.resetPasswordModal$.subscribe((visible) => {
      this.isVisible = visible;
    });
    this.subscription.add(modalSub);

    this.resetPasswordForm = this.fb.group<ResetPasswordForm>({
      email: this.fb.control('', {
        validators: [Validators.required, Validators.pattern(EMAIL_REGEX)],
      }),
    });

    this.resetPasswordForm.get('email')!.valueChanges.subscribe(() => {
      if (this.errorMessage) {
        this.errorMessage = '';
      }
    });
  }

  onSubmit(): void {
    if (this.resetPasswordForm.invalid) return;

    this.isLoading = true;
    this.errorMessage = '';

    const { email } = this.resetPasswordForm.getRawValue();
    this.userManagementService.requestPasswordReset(email).subscribe({
      next: () => {
        this.isLoading = false;
        this.isSuccessModalVisible = true;
        this.isConfirmLoading = false;
        this.isVisible = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage = err.error || 'Failed to send password reset email';
        this.snackbarService.show(this.errorMessage, 'error');
        this.isConfirmLoading = false;
      },
    });
  }

  onOverlayClickReset(): void {
    this.closeModal();
  }

  closeModal(): void {
    this.uiService.closeResetPasswordModal();
    this.isSuccessModalVisible = false;
  }

  openSignIn(): void {
    this.closeModal();
    this.uiService.openSignInModal();
  }

  get isFormInvalid(): boolean {
    return this.resetPasswordForm.invalid;
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
